import UnfoldMoreIcon from '@mui/icons-material/KeyboardArrowDown';
import UnfoldLessIcon from '@mui/icons-material/KeyboardArrowUp';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TableCell from '@mui/material/TableCell';
import { useQuery } from '@tanstack/react-query';
import { reportsService } from 'api';
import { FileSpreadsheet, FileText, File } from 'lucide-react';
import React, { useState } from 'react';
import type { DataTableColumn } from 'shared/components/DataTable';
import { DataTable, TableScrollBox } from 'shared/components/DataTable';
import { EntityLink } from 'shared/components/EntityLink';
import {
  FALLBACK_PROJECT_COLOR,
  generateDateKeys,
  granularityToGroupBy,
  resolveChartGranularity,
} from 'shared/components/charts';
import { queryKeys } from 'shared/constants/queryKeys';
import { svgToPngBase64 } from 'shared/utils/pdfBuilder';

import {
  ColumnHeaderSpan,
  DonutChartCard,
  DonutChartTitle,
  ExportMenu,
  ExportMenuIcon,
  GroupByArrowIcon,
  GroupByBar,
  GroupByChip,
  GroupByLabel,
  SubGroupCount,
  SubGroupDuration,
  SubGroupName,
  SubGroupRow,
  SubGroupTitleCell,
  SummaryChartWrapper,
  SummaryClientName,
  SummaryColorDot,
  SummaryContentRow,
  SummaryDuration,
  SummaryEntryCount,
  SummaryProjectName,
  SummaryTabRoot,
  SummaryTableScrollWrapper,
  SummaryTitleCell,
  expandIconStyle,
  groupByMenuItemItalicSx,
  groupByMenuItemSx,
  subGroupCountDefaultStyle,
} from '../reports.styles';
import { useReportsStore } from '../stores/reportsStore';
import type { GroupSlots, SummaryGroupOption } from '../stores/reportsStore';
import type { DonutSlice } from '../types/reports.types';
import {
  handleSummaryExportCSV,
  handleSummaryExportExcel,
  handleSummaryExportPDFv2,
} from '../utils/exportHelpers';
import type { ExportSummary, ProjectBarChartData, SummaryRow } from '../utils/exportHelpers';
import {
  formatDuration,
  resolveGroupLabel,
  sortSummaryRows,
  SortConfig,
  transformSummaryData,
} from '../utils/reportsUtils';

import SummaryBarChart from './charts/SummaryBarChart';
import SummaryDonutChart from './charts/SummaryDonutChart';

const SUMMARY_GROUP_OPTIONS = [
  'Project',
  'Client',
  'User',
  'Tag',
  'Description',
  'Month',
  'Week',
  'Date',
] as const;

interface SummaryTabProps {
  startDate: string;
  endDate: string;
  filters: {
    userIds?: string[];
    clientIds?: string[];
    projectIds?: string[];
    billable?: boolean | null;
    hasDescription?: boolean;
    descriptionSearch?: string;
  };
  projects: Array<{ id: string; name: string; color: string; clientId?: string }>;
  clients: Array<{ id: string; name: string }>;
  users?: Array<{ id: string; name: string }>;
  /** Controlled from parent so export menu can be triggered from the hero Export button */
  exportAnchorEl?: HTMLElement | null;
  onExportMenuClose?: () => void;
}

type DrillType = 'project' | 'client' | 'member';
function toDrillType(field: string): DrillType | null {
  if (field === 'Project') return 'project';
  if (field === 'Client') return 'client';
  if (field === 'User') return 'member';
  return null;
}

export const SummaryTab: React.FC<SummaryTabProps> = ({
  startDate,
  endDate,
  filters,
  projects,
  clients,
  users = [],
  exportAnchorEl,
  onExportMenuClose,
}) => {
  const { groupSlots, setGroupSlots } = useReportsStore();
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    column: 'duration',
    direction: 'desc',
  });
  const [groupByMenuAnchor, setGroupByMenuAnchor] = useState<{
    el: HTMLElement;
    slotIdx: 0 | 1 | 2;
  } | null>(null);
  const [expandedL1Ids, setExpandedL1Ids] = useState<Set<string>>(new Set());
  const [expandedL2Ids, setExpandedL2Ids] = useState<Set<string>>(new Set());

  const handleCollapseAll = () => {
    setExpandedL1Ids(new Set());
    setExpandedL2Ids(new Set());
  };

  const handleExportMenuClose = () => {
    onExportMenuClose?.();
  };

  const handleSort = (columnId: string) => {
    if (sortConfig.column === columnId) {
      setSortConfig({
        column: columnId,
        direction: sortConfig.direction === 'asc' ? 'desc' : 'asc',
      });
    } else {
      setSortConfig({ column: columnId, direction: 'desc' });
    }
  };

  const handleL1Expand = (e: React.MouseEvent, rowId: string) => {
    e.stopPropagation();
    setExpandedL1Ids((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const handleL2Expand = (e: React.MouseEvent, key: string) => {
    e.stopPropagation();
    setExpandedL2Ids((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleGroupSlotChange = (slotIdx: 0 | 1 | 2, field: SummaryGroupOption | null) => {
    const next: GroupSlots = [groupSlots[0], groupSlots[1], groupSlots[2]];
    if (slotIdx === 0 && field !== null) {
      next[0] = field;
      if (next[1] === field) {
        next[1] = null;
        next[2] = null;
      } else if (next[2] === field) {
        next[2] = null;
      }
    } else if (slotIdx === 1) {
      next[1] = field;
      if (field === null || field === 'Description') next[2] = null;
      else if (next[2] === field) next[2] = null;
    } else if (slotIdx === 2) {
      next[2] = field;
    }
    setGroupSlots(next);
    setGroupByMenuAnchor(null);
  };

  const groupByString = groupSlots.filter(Boolean).join(',');

  React.useEffect(() => {
    setExpandedL1Ids(new Set());
    setExpandedL2Ids(new Set());
  }, [groupByString]);

  const apiParams = {
    from: startDate,
    to: endDate,
    userIds: filters.userIds?.join(','),
    projectIds: filters.projectIds?.join(','),
    clientIds: filters.clientIds?.join(','),
    isBillable: filters.billable === null ? undefined : filters.billable,
    hasDescription: filters.hasDescription,
    descriptionSearch: filters.descriptionSearch || undefined,
    groupBy: groupByString,
  };

  const { data: summaryData, isLoading } = useQuery({
    queryKey: queryKeys.reports.summary(apiParams),
    queryFn: async () => {
      const res = await reportsService.getSummary(apiParams);
      return res.data.data;
    },
    enabled: !!startDate,
  });

  const totalDuration = summaryData?.totalDurationSecs ?? 0;
  const billableDuration = summaryData?.billableDurationSecs ?? 0;
  const amount = summaryData?.amountCents ?? 0;

  const rows: SummaryRow[] = React.useMemo(() => {
    const groups = summaryData?.groups ?? [];
    const transformed = transformSummaryData(groups, projects, clients, users);
    return sortSummaryRows(transformed, sortConfig);
  }, [summaryData, projects, clients, users, sortConfig]);

  const allL1Ids = React.useMemo(
    () => new Set(rows.filter((r) => (r.subGroups?.length ?? 0) > 0).map((r) => r.id)),
    [rows],
  );

  // All L2 keys that have further sub-groups (same format used in renderExpandedRow)
  const allL2Keys = React.useMemo(() => {
    const keys = new Set<string>();
    for (const row of rows) {
      if (!row.subGroups?.length) continue;
      row.subGroups.forEach((l1Group, l1i) => {
        if ((l1Group.subGroups?.length ?? 0) > 0) {
          keys.add(`${row.id}-${l1Group.field}-${l1Group.key ?? l1i}`);
        }
      });
    }
    return keys;
  }, [rows]);

  const isAllExpanded =
    allL1Ids.size > 0 &&
    Array.from(allL1Ids).every((id) => expandedL1Ids.has(id)) &&
    Array.from(allL2Keys).every((k) => expandedL2Ids.has(k));

  const handleExpandAllRows = () => {
    setExpandedL1Ids(new Set(allL1Ids));
    setExpandedL2Ids(new Set(allL2Keys));
  };

  // Donut slices — use project color when first groupBy is Project, else generate distinct hsl colors
  const donutSlices: DonutSlice[] = React.useMemo(() => {
    const isProjectGrouping = groupSlots[0] === 'Project';
    return rows.map((row, i) => ({
      id: row.id,
      label: row.projectName || `Item ${i + 1}`,
      color:
        isProjectGrouping && row.color && row.color !== '#ccc'
          ? row.color
          : `hsl(${(i * 47) % 360}, 60%, 55%)`,
      totalSecs: row.totalDurationSecs,
    }));
  }, [rows, groupSlots]);

  const columns: DataTableColumn<SummaryRow>[] = [
    {
      id: 'title',
      label: (
        <ColumnHeaderSpan>
          {allL1Ids.size > 0 &&
            (isAllExpanded ? (
              <UnfoldLessIcon
                fontSize="medium"
                style={expandIconStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCollapseAll();
                }}
                titleAccess="Collapse all"
              />
            ) : (
              <UnfoldMoreIcon
                fontSize="medium"
                style={expandIconStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  handleExpandAllRows();
                }}
                titleAccess="Expand all"
              />
            ))}
          Title
        </ColumnHeaderSpan>
      ),
      render: (row) => (
        <SummaryTitleCell>
          {(row.subGroups?.length ?? 0) > 0 && (
            <SummaryEntryCount onClick={(e) => handleL1Expand(e, row.id)}>
              {row.subGroups!.length}
            </SummaryEntryCount>
          )}
          {row.groupField === 'Project' && <SummaryColorDot dotcolor={row.color} />}
          <span>
            <EntityLink
              type={toDrillType(row.groupField ?? '') ?? 'description'}
              id={row.entityKey}
              startDate={startDate}
              endDate={endDate}
            >
              <SummaryProjectName>{row.projectName}</SummaryProjectName>
            </EntityLink>
            {row.clientName && ` - `}
            <SummaryClientName>{row.clientName}</SummaryClientName>
          </span>
        </SummaryTitleCell>
      ),
      sortable: true,
      sortValue: (row) => `${row.projectName}-${row.clientName}`,
    },
    {
      id: 'duration',
      label: 'Duration',
      render: (row) => <SummaryDuration>{formatDuration(row.totalDurationSecs)}</SummaryDuration>,
      sortable: true,
      sortValue: (row) => row.totalDurationSecs,
    },
  ];

  const handleExportCSV = () => {
    const summary: ExportSummary = { totalDuration, billableDuration, amount };
    const activeGroupFields = groupSlots.filter(Boolean) as string[];
    handleSummaryExportCSV(
      rows,
      summary,
      startDate,
      endDate,
      activeGroupFields,
      projects,
      clients,
      users,
    );
    handleExportMenuClose();
  };

  const handleExportExcel = () => {
    const summary: ExportSummary = { totalDuration, billableDuration, amount };
    const activeGroupFields = groupSlots.filter(Boolean) as string[];
    handleSummaryExportExcel(
      rows,
      summary,
      startDate,
      endDate,
      activeGroupFields,
      projects,
      clients,
      users,
    );
    handleExportMenuClose();
  };

  const handleExportPDF = async () => {
    handleExportMenuClose();
    const summary: ExportSummary = { totalDuration, billableDuration, amount };
    const activeGroupFields = groupSlots.filter(Boolean) as string[];

    // Fetch date × project data for the stacked bar chart
    let projectBarChartData: ProjectBarChartData | undefined;
    try {
      const granularity = resolveChartGranularity(startDate, endDate);
      const projectParams = {
        from: startDate,
        to: endDate,
        userIds: filters.userIds?.join(','),
        projectIds: filters.projectIds?.join(','),
        clientIds: filters.clientIds?.join(','),
        isBillable: filters.billable === null ? undefined : filters.billable,
        hasDescription: filters.hasDescription,
        descriptionSearch: filters.descriptionSearch || undefined,
        groupBy: granularityToGroupBy(granularity, 'Project'),
      };
      const res = await reportsService.getSummary(projectParams);
      const projectData = res.data.data;

      if (projectData?.groups) {
        // Build project id set and colour map
        const projectIds = new Set<string>();
        projectData.groups.forEach((dg: any) => {
          dg.subGroups?.forEach((pg: any) => {
            if (pg.key) projectIds.add(pg.key);
          });
        });

        const projectLegend = Array.from(projectIds).map((id) => {
          const proj = projects.find((p) => p.id === id);
          return {
            id,
            label: proj?.name ?? 'Unknown',
            color: proj?.color ?? FALLBACK_PROJECT_COLOR,
          };
        });

        const buckets = generateDateKeys(startDate, endDate, granularity);
        const byKey = new Map<string, any>();
        projectData.groups.forEach((g: any) => {
          if (g.key) byKey.set(g.key, g);
        });

        const byDate = new Map<string, Record<string, number>>();
        buckets.forEach((bucket) => {
          const dg = byKey.get(bucket);
          const projMap: Record<string, number> = {};
          dg?.subGroups?.forEach((pg: any) => {
            if (pg.key) projMap[pg.key] = pg.totalDurationSecs ?? 0;
          });
          byDate.set(bucket, projMap);
        });

        projectBarChartData = { buckets, byDate, projects: projectLegend };
      }
    } catch {
      // Silently fall back to project-total bars if fetch fails
    }

    let logoBase64: string | undefined;
    try {
      const svgRes = await fetch('/ZeLogLogo-192.svg');
      const svgText = await svgRes.text();
      const png = await svgToPngBase64(svgText, 128);
      if (png) logoBase64 = png;
    } catch {
      // Logo is optional — PDF generates fine without it
    }

    handleSummaryExportPDFv2({
      rows,
      summary,
      startDate,
      endDate,
      groupFields: activeGroupFields,
      projects,
      clients,
      users,
      projectBarChartData,
      logoBase64,
    });
  };

  return (
    <SummaryTabRoot>
      {/* Export menu — triggered from hero Export button in ReportsPage */}
      <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 100 } } }}
      >
        <ExportMenu
          onClick={() => {
            handleExportCSV();
            handleExportMenuClose();
          }}
        >
          <ExportMenuIcon>
            <FileText size={15} />
          </ExportMenuIcon>
          Export CSV
        </ExportMenu>
        <ExportMenu
          onClick={() => {
            handleExportExcel();
            handleExportMenuClose();
          }}
        >
          <ExportMenuIcon>
            <FileSpreadsheet size={15} />
          </ExportMenuIcon>
          Export Excel
        </ExportMenu>
        <ExportMenu onClick={handleExportPDF}>
          <ExportMenuIcon>
            <File size={15} />
          </ExportMenuIcon>
          Export PDF
        </ExportMenu>
      </Menu>

      {/* Chart */}
      <SummaryChartWrapper>
        <SummaryBarChart
          startDate={startDate}
          endDate={endDate}
          filters={filters}
          projects={projects}
        />
      </SummaryChartWrapper>

      {/* Group by */}
      <GroupByBar>
        <GroupByLabel>Group by:</GroupByLabel>

        {/* Slot 1 — always shown, always has a value */}
        <GroupByChip onClick={(e) => setGroupByMenuAnchor({ el: e.currentTarget, slotIdx: 0 })}>
          {groupSlots[0]}
          <GroupByArrowIcon />
        </GroupByChip>

        {/* Slot 2 — always shown, may be (None) */}
        <GroupByChip onClick={(e) => setGroupByMenuAnchor({ el: e.currentTarget, slotIdx: 1 })}>
          {groupSlots[1] ?? '(None)'}
          <GroupByArrowIcon />
        </GroupByChip>

        {/* Slot 3 — only when slot 2 is set and not a leaf field */}
        {groupSlots[1] !== null && groupSlots[1] !== 'Description' && (
          <GroupByChip onClick={(e) => setGroupByMenuAnchor({ el: e.currentTarget, slotIdx: 2 })}>
            {groupSlots[2] ?? '(None)'}
            <GroupByArrowIcon />
          </GroupByChip>
        )}

        <Menu
          anchorEl={groupByMenuAnchor?.el}
          open={Boolean(groupByMenuAnchor)}
          onClose={() => setGroupByMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {/* (None) option for slots 2 and 3 only */}
          {groupByMenuAnchor &&
            groupByMenuAnchor.slotIdx !== 0 && [
              <MenuItem
                key="none"
                selected={groupSlots[groupByMenuAnchor.slotIdx] === null}
                onClick={() => handleGroupSlotChange(groupByMenuAnchor.slotIdx, null)}
                sx={groupByMenuItemItalicSx}
              >
                (None)
              </MenuItem>,
              <Divider key="divider" />,
            ]}
          {SUMMARY_GROUP_OPTIONS.filter((f) => {
            const si = groupByMenuAnchor?.slotIdx ?? 0;
            // Only exclude values used in parent (lower-index) slots.
            // Child slots are auto-cleared by handleGroupSlotChange, so they
            // should never block options from appearing in the current slot.
            return !groupSlots.some((v: SummaryGroupOption | null, i: number) => i < si && v === f);
          }).map((f) => (
            <MenuItem
              key={f}
              selected={f === groupSlots[groupByMenuAnchor?.slotIdx ?? 0]}
              onClick={() =>
                groupByMenuAnchor && handleGroupSlotChange(groupByMenuAnchor.slotIdx, f)
              }
              sx={groupByMenuItemSx}
            >
              {f}
            </MenuItem>
          ))}
        </Menu>
      </GroupByBar>

      <SummaryContentRow>
        <SummaryTableScrollWrapper>
        <TableScrollBox maxHeight={800}>
          <DataTable<SummaryRow>
            columns={columns}
            rows={rows}
            getRowId={(row) => row.id}
            loading={isLoading}
            emptyMessage={
              isLoading ? 'Loading summary…' : 'No data for the selected date and filters.'
            }
            sortColumn={sortConfig.column}
            sortDirection={sortConfig.direction}
            onSort={handleSort}
            borderless
            headerFontSize="11px"
            headerWeight={500}
            headerColor="#5A5578"
            rowHoverColor="rgba(110, 79, 247, 0.03)"
            renderExpandedRow={(row) => {
              if (!expandedL1Ids.has(row.id) || !row.subGroups?.length) return null;
              return (
                <>
                  {row.subGroups.map((l1Group, l1i) => {
                    const l1Label = resolveGroupLabel(
                      l1Group.field,
                      l1Group.key,
                      projects,
                      clients,
                      users,
                    );
                    const l1Key = `${row.id}-${l1Group.field}-${l1Group.key ?? l1i}`;
                    const isL1Expanded = expandedL2Ids.has(l1Key);
                    const hasL2 = (l1Group.subGroups?.length ?? 0) > 0;
                    return (
                      <React.Fragment key={l1Key}>
                        <SubGroupRow depth={1}>
                          <TableCell>
                            <SubGroupTitleCell depth={1}>
                              <SubGroupCount
                                onClick={hasL2 ? (e) => handleL2Expand(e, l1Key) : undefined}
                                style={!hasL2 ? subGroupCountDefaultStyle : undefined}
                              >
                                {l1Group.subGroups?.length ?? l1Group.entryCount}
                              </SubGroupCount>
                              <EntityLink
                                type={toDrillType(l1Group.field) ?? 'description'}
                                id={l1Group.key}
                                startDate={startDate}
                                endDate={endDate}
                              >
                                <SubGroupName>{l1Label}</SubGroupName>
                              </EntityLink>
                            </SubGroupTitleCell>
                          </TableCell>
                          <TableCell>
                            <SubGroupDuration>
                              {formatDuration(l1Group.totalDurationSecs)}
                            </SubGroupDuration>
                          </TableCell>
                        </SubGroupRow>
                        {isL1Expanded &&
                          l1Group.subGroups?.map((l2Group, l2i) => {
                            const l2Label = resolveGroupLabel(
                              l2Group.field,
                              l2Group.key,
                              projects,
                              clients,
                              users,
                            );
                            return (
                              <SubGroupRow
                                key={`${l1Key}-${l2Group.field}-${l2Group.key ?? l2i}`}
                                depth={2}
                              >
                                <TableCell>
                                  <SubGroupTitleCell depth={2}>
                                    <EntityLink
                                      type={toDrillType(l2Group.field) ?? 'description'}
                                      id={l2Group.key}
                                      startDate={startDate}
                                      endDate={endDate}
                                    >
                                      <SubGroupName>{l2Label}</SubGroupName>
                                    </EntityLink>
                                  </SubGroupTitleCell>
                                </TableCell>
                                <TableCell>
                                  <SubGroupDuration>
                                    {formatDuration(l2Group.totalDurationSecs)}
                                  </SubGroupDuration>
                                </TableCell>
                              </SubGroupRow>
                            );
                          })}
                      </React.Fragment>
                    );
                  })}
                </>
              );
            }}
          />
        </TableScrollBox>
        </SummaryTableScrollWrapper>

        {/* Donut chart */}
        <DonutChartCard>
          <DonutChartTitle sx={{ color: 'text.primary' }}>Project Distribution</DonutChartTitle>
          <SummaryDonutChart slices={donutSlices} totalSecs={totalDuration} />
        </DonutChartCard>
      </SummaryContentRow>
    </SummaryTabRoot>
  );
};
